// One collider (APP_BAKER §3 "One collider") — PURE module shared by the WebXR client
// (esbuild import), the game server (require) and the gates, in the
// lifts.js/spans.js mould: dependency-free, no THREE, no fs. It collides a
// body with the structures.bin triangle soup (game/structures.js) — the SAME
// triangles S7 draws — and answers:
//   groundAt   highest walkable floor within the step-up (point-exact)
//   ceilingAt  lowest ceiling over the step-up (point-exact head clamp)
//   slide      2D swept collide-and-slide of the body circle (time of impact)
//   move       the full frame: floor/head sweeps + ceiling clamp + substepped
//              slide + static push-out — entry is PREVENTED, not repaired
//   crosses    the server's conservative "centre path crossed a blocker" test
//   raycast    bullets / bot line of sight (both-sided Möller–Trumbore)
// Terrain stays the heightfield and air inside terrain stays spans: this
// module never sees either. Structures are ADDITIVE — callers compose
// ground = max(terrain/spans ground, groundAt) (APP_BAKER §3).
//
// Body model (the Player's, unchanged): a vertical cylinder of radius
// R_BODY = 0.35 from the feet to the head (eye*0.92). The LATERAL band is
// (feet + STEP_UP + STEP_EPS, head): every collidable triangle is solid where
// it intrudes into that band, from both sides (a zero-thickness wall blocks).
// What lies below the band is stepped over (a WALK floor there is ground),
// what lies above it is overhead (a CEIL/WALK sheet there clamps the head).
// The triangle is clipped to the band and projected to xz: a convex polygon
// (a segment for a vertical wall). Sweeping a circle against it is a ray vs
// the polygon's Minkowski sum with the disc — every edge offset ±R on BOTH
// sides plus a disc at every vertex — so no orientation or winding matters.
'use strict';

const { TF, KINDS, isRailing } = require('./structures.js');

const R_BODY = 0.35;        // == BuildingCollision radius
const STEP_UP = 0.35;       // == spans STEP_TOL, the building step-up
const STEP_EPS = 1e-3;      // one threshold for "claimable" vs "in the band"; absorbs f32 of the file
const SKIN = 1e-3;          // sweep stand-off (m)
const TOUCH = 1e-4;         // "in contact" slack on the distance test
const ON_EPS = 0.01;        // a sweep only crosses a plane it started this far from
const BARY_EPS = 1e-7;      // point-in-triangle slack (shared edges resolve to either side)
const SLOPE_WALK_DEG = 42;  // tunables locomotion.slope_walk_max_deg
const SLOPE_STAIR_DEG = 45; // TF.STAIR floors (100 %)
const TELEPORT_M = 60;      // a longer frame is a beam, not motion (== Player._sweepIn)
const SUBSTEP_DY = 0.3;     // lateral substep per this much vertical travel
const SUBSTEP_MAX = 12;
const DEFAULT_CELL = 8;     // grid cell (m) — chosen by check-structcol's bench
const SERVER_BAND_H = 1.0;  // crosses(): the crouched body (bore_eye_height 1.15 * 0.92)
const EXIT_EPS = 1e-6;
const BOX_EPS = 1e-5;       // > EXIT_EPS: a polygon box prefilter never drops an EXIT_EPS / closed hit      // a chord crossing this close to an enclosing polygon's edge is the exit from it

// derived per-triangle roles
const RL_FLOOR = 1;   // WALK and not steeper than its slope limit
const RL_CEIL = 2;    // CEIL or WALK, not vertical: clamps a head from below
const RL_COLL = 4;    // collidable at all (not NO_COLLIDE)
// (bridge railings, owner 2026-10-16) an open railing (STR.isRailing: kind
// parapet, collide-only): a body stops at it, a bullet passes it —
// raycast(..., RL_SHOT_PASS) skips it (game/buildcol.js Occlusion)
const RL_SHOT_PASS = 8;

// scratch (module-level: the collider is single-threaded on every end)
const _ax = new Float64Array(8), _ay = new Float64Array(8), _az = new Float64Array(8);
const _bx = new Float64Array(8), _by = new Float64Array(8), _bz = new Float64Array(8);

// distance from (px,pz) to the segment a->b
function segDist(px, pz, ax, az, bx, bz) {
  const ex = bx - ax, ez = bz - az, L2 = ex * ex + ez * ez;
  const u = L2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * ex + (pz - az) * ez) / L2)) : 0;
  return Math.hypot(px - ax - ex * u, pz - az - ez * u);
}

function clipPlane(sx, sy, sz, n, h, sign, dx, dy, dz) {
  let m = 0;
  for (let i = 0; i < n; i++) {
    const j = i + 1 === n ? 0 : i + 1;
    const di = (sy[i] - h) * sign, dj = (sy[j] - h) * sign;
    if (di > 0) { dx[m] = sx[i]; dy[m] = sy[i]; dz[m] = sz[i]; m++; }
    if ((di > 0) !== (dj > 0) && di !== dj) {
      const u = di / (di - dj);
      dx[m] = sx[i] + (sx[j] - sx[i]) * u;
      dy[m] = h;
      dz[m] = sz[i] + (sz[j] - sz[i]) * u;
      m++;
    }
  }
  return m;
}

const STEP_TRIS = 16383;   // StructCollider.build: a yield every 2^14 triangles (a mask)
class StructCollider {
  // str: loadStructures() result (or null = empty). opt: { cell, slopeWalkDeg }
  constructor(str, opt = {}) {
    const it = this._init(str, opt);
    while (!it.next().done) { /* drain */ }
  }

  // the same build as a STEP GENERATOR (client-load-unresponsive): yields
  // every STEP_TRIS triangles of its two passes, returns the collider — the
  // client (main.js) spreads a 300 k-triangle index over main-thread tasks;
  // the server keeps `new` (one go). The same instance either way.
  static *build(str, opt = {}) {
    const c = Object.create(StructCollider.prototype);
    yield* c._init(str, opt);
    return c;
  }

  *_init(str, opt) {
    this.str = str || null;
    this.cell = opt.cell || DEFAULT_CELL;
    const nT = str ? str.nTris : 0;
    this.nTris = nT;
    this.V = str ? str.verts : new Float32Array(0);
    this.I = str ? str.idx : new Uint32Array(0);
    const cosWalk = Math.cos((opt.slopeWalkDeg ?? SLOPE_WALK_DEG) * Math.PI / 180);
    const cosStair = Math.cos((opt.slopeStairDeg ?? SLOPE_STAIR_DEG) * Math.PI / 180);
    this.ymin = new Float32Array(nT);
    this.ymax = new Float32Array(nT);
    this.role = new Uint8Array(nT);
    this.stamp = new Uint16Array(nT);    // per-query dedup (wraps: refilled every 65 535 queries)
    this._sid = 0;
    this._cand = new Uint32Array(256);
    this._poly = new Float64Array(16);   // one clipped polygon (xz) scratch
    this._pb = new Float64Array(640);    // slide(): the call's clipped polygons
    this._po = new Int32Array(64);       //   offsets into _pb
    this._pn = new Uint8Array(64);       //   vertex counts
    this._c = { d: 0, nx: 0, nz: 0 };    // contact scratch
    this._s = { t: 0, nx: 0, nz: 0 };    // sweep scratch
    this._o = { x: 0, z: 0, hit: false, nx: 0, nz: 0, moved: 0 };
    this._kd = new Float64Array(64);     // depenetrate(): the iteration's contacts (depth, normal)
    this._kx = new Float64Array(64);
    this._kz = new Float64Array(64);
    this._qb = new Float64Array(640);    // _bandPolys(): a query's clipped polygons (_pushCrosses, _solidExit)
    this._qo = new Int32Array(64);       //   offsets into _qb
    this._qn = new Uint8Array(64);       //   vertex counts
    this._qm = new Uint8Array(64);       //   1 = a member of the solid the query point is inside (_solidOf)
    this._qx = new Float64Array(256);    //   (_solidOf) each polygon's xz box: x0, z0, x1, z1
    this._rs = new Float64Array(32);     // _solidExit(): the ray's crossings (distance, polygon, edge)
    this._rp = new Int32Array(32);
    this._re = new Int32Array(32);
    this._hx = { nx: 0, nz: 0, d: 0 };   // _solidExit() result
    this._qKeep = -1;                    // _qb holds depenetrate()'s band polygons for the box _q{x,z}{0,1} (-1: not)
    this._qx0 = 0; this._qz0 = 0; this._qx1 = 0; this._qz1 = 0;
    const V = this.V, I = this.I, fl = str ? str.tflags : null;
    const cell = this.cell;
    const counts = new Map();
    let nColl = 0, sOf = 0;
    // (bridge railings) the record of triangle t (a bare triangle soup
    // without records: no railing)
    const kPar = KINDS.indexOf('parapet'), sKind = str && str.kind && str.firstTri ? str.kind : null;
    for (let t = 0; t < nT; t++) {
      if ((t & STEP_TRIS) === STEP_TRIS) yield;
      while (sKind && sOf < str.nStruct && str.firstTri[sOf] + str.nTri[sOf] <= t) sOf++;
      const a = I[t * 3] * 3, b = I[t * 3 + 1] * 3, c = I[t * 3 + 2] * 3;
      this.ymin[t] = Math.min(V[a + 1], V[b + 1], V[c + 1]);
      this.ymax[t] = Math.max(V[a + 1], V[b + 1], V[c + 1]);
      const f = fl[t];
      if (f & TF.NO_COLLIDE) continue;
      const e1x = V[b] - V[a], e1y = V[b + 1] - V[a + 1], e1z = V[b + 2] - V[a + 2];
      const e2x = V[c] - V[a], e2y = V[c + 1] - V[a + 1], e2z = V[c + 2] - V[a + 2];
      const nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
      const nl = Math.hypot(nx, ny, nz);
      if (!(nl > 1e-12)) continue;                 // degenerate: no area, no collision
      const cy = Math.abs(ny) / nl;
      let r = RL_COLL;
      if ((f & TF.WALK) && cy >= ((f & TF.STAIR) ? cosStair : cosWalk) - 1e-9) r |= RL_FLOOR;
      if ((f & (TF.CEIL | TF.WALK)) && cy > 1e-4) r |= RL_CEIL;
      if (sKind && sKind[sOf] === kPar && isRailing(kPar, f)) r |= RL_SHOT_PASS;
      this.role[t] = r;
      nColl++;
      const x0 = Math.min(V[a], V[b], V[c]), x1 = Math.max(V[a], V[b], V[c]);
      const z0 = Math.min(V[a + 2], V[b + 2], V[c + 2]), z1 = Math.max(V[a + 2], V[b + 2], V[c + 2]);
      for (let iz = Math.floor(z0 / cell); iz <= Math.floor(z1 / cell); iz++) {
        for (let ix = Math.floor(x0 / cell); ix <= Math.floor(x1 / cell); ix++) {
          const k = StructCollider._key(ix, iz);
          counts.set(k, (counts.get(k) || 0) + 1);
        }
      }
    }
    this.nColl = nColl;
    // open-addressing hash of occupied cells -> [off, off + cnt) in cellTris
    let size = 16;
    while (size < counts.size * 2) size <<= 1;
    this._mask = size - 1;
    this._shift = 32 - Math.log2(size);
    this.hkey = new Uint32Array(size);
    this.hoff = new Uint32Array(size);
    this.hcnt = new Uint32Array(size);
    let total = 0;
    const slotOf = new Map();
    for (const [k, n] of counts) {
      let h = Math.imul(k, 0x9E3779B1) >>> this._shift;
      while (this.hcnt[h] !== 0) h = (h + 1) & this._mask;
      this.hkey[h] = k;
      this.hoff[h] = total;
      this.hcnt[h] = n;       // provisional (non-zero marks the slot used)
      slotOf.set(k, h);
      total += n;
    }
    this.cellTris = new Uint32Array(total);
    const fill = new Uint32Array(size);
    yield;
    for (let t = 0; t < nT; t++) {
      if ((t & STEP_TRIS) === STEP_TRIS) yield;
      if (!(this.role[t] & RL_COLL)) continue;
      const a = I[t * 3] * 3, b = I[t * 3 + 1] * 3, c = I[t * 3 + 2] * 3;
      const x0 = Math.min(V[a], V[b], V[c]), x1 = Math.max(V[a], V[b], V[c]);
      const z0 = Math.min(V[a + 2], V[b + 2], V[c + 2]), z1 = Math.max(V[a + 2], V[b + 2], V[c + 2]);
      for (let iz = Math.floor(z0 / cell); iz <= Math.floor(z1 / cell); iz++) {
        for (let ix = Math.floor(x0 / cell); ix <= Math.floor(x1 / cell); ix++) {
          const h = slotOf.get(StructCollider._key(ix, iz));
          this.cellTris[this.hoff[h] + fill[h]++] = t;
        }
      }
    }
    this.nCells = counts.size;
    this.nRefs = total;
    let maxCell = 0;
    for (const n of counts.values()) if (n > maxCell) maxCell = n;
    this.maxCell = maxCell;
  }

  static _key(ix, iz) { return (((ix & 0xFFFF) << 16) | (iz & 0xFFFF)) >>> 0; }

  _slot(ix, iz) {
    const k = StructCollider._key(ix, iz);
    let h = Math.imul(k, 0x9E3779B1) >>> this._shift;
    for (;;) {
      if (this.hcnt[h] === 0) return -1;
      if (this.hkey[h] === k) return h;
      h = (h + 1) & this._mask;
    }
  }

  // unique collidable triangles whose cells touch [x0,x1]x[z0,z1] -> this._cand[0..n)
  _gather(x0, z0, x1, z1) {
    if (this.nRefs === 0) return 0;
    const c = this.cell;
    const ix0 = Math.floor(x0 / c), ix1 = Math.floor(x1 / c);
    const iz0 = Math.floor(z0 / c), iz1 = Math.floor(z1 / c);
    if (++this._sid === 0x10000) { this.stamp.fill(0); this._sid = 1; }
    const sid = this._sid, st = this.stamp;
    let n = 0;
    for (let iz = iz0; iz <= iz1; iz++) {
      for (let ix = ix0; ix <= ix1; ix++) {
        const h = this._slot(ix, iz);
        if (h < 0) continue;
        const o = this.hoff[h], e = o + this.hcnt[h];
        for (let k = o; k < e; k++) {
          const t = this.cellTris[k];
          if (st[t] === sid) continue;
          st[t] = sid;
          if (n === this._cand.length) {
            const g = new Uint32Array(n * 2); g.set(this._cand); this._cand = g;
          }
          this._cand[n++] = t;
        }
      }
    }
    return n;
  }

  // plane height of triangle t at (x,z) if (x,z) lies inside its xz
  // projection, else NaN. Vertical triangles answer NaN.
  _heightAt(t, x, z) {
    const V = this.V, I = this.I;
    const a = I[t * 3] * 3, b = I[t * 3 + 1] * 3, c = I[t * 3 + 2] * 3;
    const ax = V[a], az = V[a + 2], bx = V[b], bz = V[b + 2], cx = V[c], cz = V[c + 2];
    const d = (bz - cz) * (ax - cx) + (cx - bx) * (az - cz);
    if (Math.abs(d) < 1e-12) return NaN;
    const l1 = ((bz - cz) * (x - cx) + (cx - bx) * (z - cz)) / d;
    const l2 = ((cz - az) * (x - cx) + (ax - cx) * (z - cz)) / d;
    const l3 = 1 - l1 - l2;
    if (l1 < -BARY_EPS || l2 < -BARY_EPS || l3 < -BARY_EPS) return NaN;
    return l1 * V[a + 1] + l2 * V[b + 1] + l3 * V[c + 1];
  }

  // ---------------------------------------------------------- point queries
  // Highest walkable floor at (x,z) that a body with its feet at `feetY` can
  // claim: y <= feetY + STEP_UP (+ STEP_EPS). null = no structure floor.
  // Point-exact like roofSpanAt and the spans resolver: the feet are a point.
  groundAt(x, z, feetY) {
    if (this.nRefs === 0) return null;
    const h = this._slot(Math.floor(x / this.cell), Math.floor(z / this.cell));
    if (h < 0) return null;
    const lim = feetY + STEP_UP + STEP_EPS;
    let best = null;
    for (let k = this.hoff[h], e = k + this.hcnt[h]; k < e; k++) {
      const t = this.cellTris[k];
      if (!(this.role[t] & RL_FLOOR) || this.ymin[t] > lim) continue;
      const y = this._heightAt(t, x, z);
      if (y <= lim && (best === null || y > best)) best = y;
    }
    return best;
  }

  // Lowest CEIL/WALK sheet over (x,z) ABOVE the claimable step (> feetY +
  // STEP_UP + STEP_EPS): the head clamps to it. null = open overhead.
  ceilingAt(x, z, feetY) {
    if (this.nRefs === 0) return null;
    const h = this._slot(Math.floor(x / this.cell), Math.floor(z / this.cell));
    if (h < 0) return null;
    const lim = feetY + STEP_UP + STEP_EPS;
    let best = null;
    for (let k = this.hoff[h], e = k + this.hcnt[h]; k < e; k++) {
      const t = this.cellTris[k];
      if (!(this.role[t] & RL_CEIL) || this.ymax[t] <= lim) continue;
      const y = this._heightAt(t, x, z);
      if (y > lim && (best === null || y < best)) best = y;
    }
    return best;
  }

  // ------------------------------------------------------ band polygons
  // does triangle t's xz bounding box meet [x0,x1]x[z0,z1]?
  _near(t, x0, z0, x1, z1) {
    const V = this.V, I = this.I;
    const a = I[t * 3] * 3, b = I[t * 3 + 1] * 3, c = I[t * 3 + 2] * 3;
    const ax = V[a], bx = V[b], cx = V[c];
    if ((ax < x0 && bx < x0 && cx < x0) || (ax > x1 && bx > x1 && cx > x1)) return false;
    const az = V[a + 2], bz = V[b + 2], cz = V[c + 2];
    return !((az < z0 && bz < z0 && cz < z0) || (az > z1 && bz > z1 && cz > z1));
  }

  // clip triangle t to the open band (ylo, yhi), project to xz -> P[o..],
  // returns the vertex count (0 = no intrusion)
  _clip(t, ylo, yhi, P = this._poly, o = 0) {
    const V = this.V, I = this.I;
    for (let c = 0; c < 3; c++) {
      const v = I[t * 3 + c] * 3;
      _ax[c] = V[v]; _ay[c] = V[v + 1]; _az[c] = V[v + 2];
    }
    let n = clipPlane(_ax, _ay, _az, 3, ylo, 1, _bx, _by, _bz);
    if (n === 0) return 0;
    n = clipPlane(_bx, _by, _bz, n, yhi, -1, _ax, _ay, _az);
    let m = 0;
    for (let i = 0; i < n; i++) {
      const x = _ax[i], z = _az[i];
      if (m > 0 && Math.abs(x - P[o + m * 2 - 2]) < 1e-9 && Math.abs(z - P[o + m * 2 - 1]) < 1e-9) continue;
      P[o + m * 2] = x; P[o + m * 2 + 1] = z; m++;
    }
    if (m > 1 && Math.abs(P[o] - P[o + m * 2 - 2]) < 1e-9 && Math.abs(P[o + 1] - P[o + m * 2 - 1]) < 1e-9) m--;
    return m;
  }

  // distance from (px,pz) to the polygon P[o..] (n vertices) -> this._c
  // { d (>= 0 outside, < 0 = inside by |d| to the nearest edge), nx,nz (unit,
  // pointing from the polygon toward the point / out of it) }. A zero-distance
  // contact with a degenerate (segment) polygon takes the side of (hx,hz).
  _contact(P, o, n, px, pz, hx, hz) {
    const C = this._c;
    let best = Infinity, qx = P[o + 0], qz = P[o + 1], ex = 0, ez = 0;
    if (n === 1) {
      best = Math.hypot(px - qx, pz - qz);
    } else {
      const ne = n === 2 ? 1 : n;
      for (let i = 0; i < ne; i++) {
        const j = i + 1 === n ? 0 : i + 1;
        const ax = P[o + i * 2], az = P[o + i * 2 + 1], bx = P[o + j * 2], bz = P[o + j * 2 + 1];
        const dx = bx - ax, dz = bz - az;
        const L2 = dx * dx + dz * dz;
        let u = L2 > 0 ? ((px - ax) * dx + (pz - az) * dz) / L2 : 0;
        u = u < 0 ? 0 : u > 1 ? 1 : u;
        const cx = ax + dx * u, cz = az + dz * u;
        const d = Math.hypot(px - cx, pz - cz);
        if (d < best) { best = d; qx = cx; qz = cz; ex = dx; ez = dz; }
      }
    }
    // inside a proper (non-degenerate) polygon?
    let inside = false;
    if (n >= 3) {
      let area = 0;
      for (let i = 0; i < n; i++) {
        const j = i + 1 === n ? 0 : i + 1;
        area += P[o + i * 2] * P[o + j * 2 + 1] - P[o + j * 2] * P[o + i * 2 + 1];
      }
      if (Math.abs(area) > 1e-8) {
        inside = true;
        for (let i = 0; i < n && inside; i++) {
          const j = i + 1 === n ? 0 : i + 1;
          const cr = (P[o + j * 2] - P[o + i * 2]) * (pz - P[o + i * 2 + 1]) - (P[o + j * 2 + 1] - P[o + i * 2 + 1]) * (px - P[o + i * 2]);
          if (cr * area < 0) inside = false;
        }
      }
    }
    if (best > 1e-9) {
      const s = inside ? -1 : 1;   // inside: outward = from the point toward the edge
      C.nx = s * (px - qx) / best; C.nz = s * (pz - qz) / best;
      C.d = inside ? -best : best;
      return C;
    }
    // exactly on the boundary: the edge normal, toward the hint (thin wall:
    // the side the body came from) or away from the centroid
    let nx = -ez, nz = ex;
    const nl = Math.hypot(nx, nz);
    if (nl > 0) { nx /= nl; nz /= nl; } else { nx = 1; nz = 0; }
    let rx = hx - px, rz = hz - pz;
    if (n >= 3 && inside) {
      let gx = 0, gz = 0;
      for (let i = 0; i < n; i++) { gx += P[o + i * 2]; gz += P[o + i * 2 + 1]; }
      rx = px - gx / n; rz = pz - gz / n;
    }
    if (nx * rx + nz * rz < 0) { nx = -nx; nz = -nz; }
    C.nx = nx; C.nz = nz; C.d = 0;
    return C;
  }

  // earliest t in [0, tMax] at which a circle of radius R, centre moving
  // (px,pz) + t*(mx,mz), touches the polygon; Infinity if never. The start
  // must be clear of it (the caller tests contact first).
  _sweep(P, o, n, px, pz, mx, mz, R, tMax) {
    const S = this._s;
    let best = Infinity;
    const ne = n === 1 ? 0 : n === 2 ? 1 : n;
    for (let i = 0; i < ne; i++) {
      const j = i + 1 === n ? 0 : i + 1;
      const ax = P[o + i * 2], az = P[o + i * 2 + 1], bx = P[o + j * 2], bz = P[o + j * 2 + 1];
      const ex = bx - ax, ez = bz - az;
      const L = Math.hypot(ex, ez);
      if (L < 1e-12) continue;
      const ux = -ez / L, uz = ex / L;
      for (let s = 1; s >= -1; s -= 2) {
        const nx = ux * s, nz = uz * s;
        const den = mx * nx + mz * nz;
        if (den >= 0) continue;
        const d0 = (px - ax) * nx + (pz - az) * nz - R;
        if (d0 < 0) continue;
        const t = -d0 / den;
        if (t > tMax || t >= best) continue;
        const hx = px + mx * t - ax, hz = pz + mz * t - az;
        const u = (hx * ex + hz * ez) / (L * L);
        if (u < 0 || u > 1) continue;
        best = t; S.nx = nx; S.nz = nz;
      }
    }
    const a = mx * mx + mz * mz;
    if (a > 0) {
      for (let i = 0; i < n; i++) {
        const fx = px - P[o + i * 2], fz = pz - P[o + i * 2 + 1];
        const c = fx * fx + fz * fz - R * R;
        if (c < 0) continue;
        const b = fx * mx + fz * mz;
        if (b >= 0) continue;
        const disc = b * b - a * c;
        if (disc < 0) continue;
        const t = (-b - Math.sqrt(disc)) / a;
        if (t < 0 || t > tMax || t >= best) continue;
        best = t;
        const hx = fx + mx * t, hz = fz + mz * t;
        const hl = Math.hypot(hx, hz) || 1;
        S.nx = hx / hl; S.nz = hz / hl;
      }
    }
    S.t = best;
    return best;
  }

  // ---------------------------------------------------------- lateral
  // Swept collide-and-slide of a circle (radius R) from (x,z) by (mx,mz) in
  // the band (ylo, yhi). -> this._o { x, z, hit, nx, nz } (nx,nz = the FIRST
  // contact normal). Every advance is time-of-impact checked against every
  // polygon, so no displacement, however large, passes through a wall.
  slide(x, z, mx, mz, ylo, yhi, R = R_BODY, out = this._o) {
    out.hit = false; out.nx = 0; out.nz = 0;
    const len0 = Math.hypot(mx, mz);
    if (this.nRefs === 0 || len0 < 1e-9) { out.x = x + mx; out.z = z + mz; return out; }
    // every slid remainder is shorter than the motion, so the whole call
    // stays in the disc of radius |m| round the start: gather and clip ONCE
    const pad = len0 + R + SKIN;
    const qx0 = x - pad, qz0 = z - pad, qx1 = x + pad, qz1 = z + pad;
    const n = this._gather(qx0, qz0, qx1, qz1);
    const cand = this._cand;
    let np = 0, off = 0;
    for (let k = 0; k < n; k++) {
      const t = cand[k];
      if (!(this.ymin[t] < yhi && this.ymax[t] > ylo) || !this._near(t, qx0, qz0, qx1, qz1)) continue;
      if (off + 10 > this._pb.length) { const g = new Float64Array(this._pb.length * 2); g.set(this._pb); this._pb = g; }
      const pn = this._clip(t, ylo, yhi, this._pb, off);
      if (pn === 0) continue;
      if (np === this._po.length) {
        const g = new Int32Array(np * 2); g.set(this._po); this._po = g;
        const h = new Uint8Array(np * 2); h.set(this._pn); this._pn = h;
      }
      this._po[np] = off; this._pn[np] = pn; np++;
      off += pn * 2;
    }
    const PB = this._pb, PO = this._po, PN = this._pn;
    let free = np === 0;
    for (let it = 0; it < 4 && np > 0; it++) {
      const L = Math.hypot(mx, mz);
      if (L < 1e-9) break;
      let tBest = 1, nxB = 0, nzB = 0, hitB = false;
      for (let k = 0; k < np; k++) {
        const o = PO[k], pn = PN[k];
        const C = this._contact(PB, o, pn, x, z, x - mx, z - mz);
        if (C.d < R + TOUCH) {
          // touching / overlapping at the start: block only the inward part
          if (mx * C.nx + mz * C.nz < -1e-12) {
            tBest = 0; nxB = C.nx; nzB = C.nz; hitB = true;
          }
          continue;
        }
        if (C.d > R + L * tBest + SKIN) continue;   // out of reach this iteration
        const t = this._sweep(PB, o, pn, x, z, mx, mz, R, tBest);
        if (t <= tBest && t !== Infinity) {
          tBest = t; nxB = this._s.nx; nzB = this._s.nz; hitB = true;
        }
      }
      if (!hitB) { free = true; break; }
      const tt = Math.max(0, tBest - SKIN / L);
      x += mx * tt; z += mz * tt;
      if (!out.hit) { out.hit = true; out.nx = nxB; out.nz = nzB; }
      // the rest of the motion, minus its component into the contact
      let rx = mx * (1 - tt), rz = mz * (1 - tt);
      const vn = rx * nxB + rz * nzB;
      if (vn < 0) { rx -= vn * nxB; rz -= vn * nzB; }
      mx = rx; mz = rz;
    }
    // the unobstructed remainder (after 4 contacts the rest is dropped)
    if (free) { x += mx; z += mz; }
    out.x = x; out.z = z;
    return out;
  }

  // Static push-out: while the circle overlaps a band polygon, move it out
  // along the deepest contact, at most `budget` metres in total. A thin wall
  // is left on the side of (hx,hz) — where the body came from.
  //
  // THE RULE (COL, APP_BAKER §3 "One collider"): a push-out never carries the centre THROUGH a
  // blocker, and the exit side is the side the body came from.
  //  - Every step is tested as the chord from the call's ENTRY centre to the
  //    step's end (_pushCrosses: crosses() semantics in this band — a
  //    segment crossed, or the end inside a proper polygon — except the EXIT
  //    from the solid the entry is inside, through that solid's boundary).
  //    A step that would cross is REFUSED and the next-deepest contact is
  //    tried; when none passes, the call stops and the body stays where the
  //    slide left it: on the side it came from, overlapping, its centre
  //    outside the wall (the slide's time of impact put it there; the slide
  //    blocks the inward part of every later motion; it walks or falls clear).
  //  - EMBEDDED (the centre inside a solid's band footprint: move() does it
  //    in a descending frame whose earlier substep, or earlier frame, carried
  //    the centre over a wall top below the band that the final band meets
  //    — a slow hop over a parapet, check-structcol 'move() embeds'): the
  //    body leaves through a FACE of the solid (_solidExit: an edge no other
  //    member covers — never a top quad's diagonal or a strip joint),
  //    perpendicular to it: the face the chord centre -> hint leaves the
  //    solid through when the hint (the frame start) lies outside every
  //    solid — the side it came from; the NEAREST face only when the hint
  //    gives no side (on / inside a solid too: a straight drop, a hop's
  //    frame that started over the top) or that exit is refused. Past the
  //    face it is an overlap: the deepest contact pushes on off that face.
  // Recorded case (bake ba3873b0, check-traversal 'never through a wall'):
  // a path walker between an embankment side slope and a 0.16 m parapet;
  // the slope's contact (the deepest) pushed the centre 0.225 m INTO the
  // parapet top, where the top TRIANGLE's nearest edge was the quad's
  // internal diagonal, so the next step pushed on through the far face:
  // 0.7 m in one frame (scripts/fixtures/collider.json replays it).
  // Chosen over "pick the exit edge on the hint side in _contact's inside
  // branch when the polygon is thinner than 2R": (1) it misses the recorded
  // frame's first step — the centre was OUTSIDE every polygon, the push
  // came from the other structure's contact; (2) a band polygon is one
  // TRIANGLE, which cannot tell a real face from its triangulation's
  // internal diagonal, so "thinner than 2R" is ill-posed (a 10 m slab's
  // triangle is thin at its acute corner too) — the solid is the union of
  // touching triangles (_solidOf); (3) _contact is shared with slide() and
  // the server's crosses(), whose outputs would move with it. Nothing
  // changes where no push would cross and the centre is not embedded: the
  // deepest contact is tried first (same ties, same arithmetic), so every
  // such push is the pre-rule push, bit for bit.
  depenetrate(x, z, ylo, yhi, R, hx, hz, budget, out = this._o) {
    out.moved = 0; out.hit = false; out.nx = 0; out.nz = 0;
    if (this.nRefs === 0) { out.x = x; out.z = z; return out; }
    const x0 = x, z0 = z;
    let hintFree = -1;   // is the hint outside every solid? (-1 = not asked yet)
    for (let it = 0; it < 4; it++) {
      // the band polygons around the circle, kept in _qb for this
      // iteration's _pushCrosses (a chord inside this box needs no other)
      const np = this._bandPolys(this._gather(x - R, z - R, x + R, z + R), ylo, yhi, x - R, z - R, x + R, z + R, 1);
      const QB = this._qb;
      let m = 0, emb = false;
      for (let q = 0; q < np; q++) {
        const o = this._qo[q], pn = this._qn[q];
        const C = this._contact(QB, o, pn, x, z, hx, hz);
        const depth = R - C.d;
        if (!(depth > TOUCH)) continue;
        if (m === this._kd.length) {
          const g = new Float64Array(m * 2); g.set(this._kd); this._kd = g;
          const gx = new Float64Array(m * 2); gx.set(this._kx); this._kx = gx;
          const gz = new Float64Array(m * 2); gz.set(this._kz); this._kz = gz;
        }
        this._kd[m] = depth; this._kx[m] = C.nx; this._kz[m] = C.nz; m++;
        if (!emb && C.d <= 0 && (C.d < 0 || this._encloses(QB, o, pn, x, z))) emb = true;
      }
      this._qKeep = np; this._qx0 = x - R; this._qz0 = z - R; this._qx1 = x + R; this._qz1 = z + R;
      // embedded: out through the SOLID's face — the one toward the hint when
      // the hint gives a side, else the nearest one; never a triangle's
      // internal edge (a top quad's diagonal, a strip joint)
      if (emb && budget - out.moved > 0) {
        if (hintFree < 0) hintFree = Math.hypot(hx - x, hz - z) > 1e-6 && this._hintFree(hx, hz, ylo, yhi) ? 1 : 0;
        const room = budget - out.moved;
        if ((hintFree === 1 && this._solidExit(x, z, hx, hz, ylo, yhi) && this._exitStep(x0, z0, x, z, room, ylo, yhi, out))
          || (this._solidExit(x, z, x, z, ylo, yhi, room) && this._exitStep(x0, z0, x, z, room, ylo, yhi, out))) {
          x = out.x; z = out.z;
          continue;
        }
      }
      const KD = this._kd;
      let done = true;
      for (;;) {
        // the deepest untried contact (strict >: the first of equals wins)
        let b = -1, deep = TOUCH;
        for (let i = 0; i < m; i++) if (KD[i] > deep) { deep = KD[i]; b = i; }
        if (b < 0) break;
        const step = Math.min(deep + SKIN, budget - out.moved);
        if (step <= 0) break;
        const nx = this._kx[b], nz = this._kz[b];
        if (this._pushCrosses(x0, z0, x + nx * step, z + nz * step, ylo, yhi)) { KD[b] = -1; continue; }
        x += nx * step; z += nz * step;
        out.moved += step;
        if (!out.hit) { out.hit = true; out.nx = nx; out.nz = nz; }
        done = false;
        break;
      }
      if (done) break;
    }
    out.x = x; out.z = z;
    return out;
  }

  // is (px,pz) in the CLOSED proper polygon P[o..o+pn) — inside or on its
  // boundary? (_contact answers d === 0 exactly on the boundary: a body on
  // the joint of two top triangles is inside the solid, not outside both)
  _encloses(P, o, pn, px, pz) {
    if (pn < 3) return false;
    let area = 0;
    for (let i = 0; i < pn; i++) {
      const j = i + 1 === pn ? 0 : i + 1;
      area += P[o + i * 2] * P[o + j * 2 + 1] - P[o + j * 2] * P[o + i * 2 + 1];
    }
    return Math.abs(area) > 1e-8 && this._contact(P, o, pn, px, pz, px, pz).d <= 0;
  }

  // clip every gathered candidate (this._cand[0..n)) meeting the band and the
  // box into this._qb (offsets _qo, counts _qn; points dropped) -> the count
  _bandPolys(n, ylo, yhi, bx0, bz0, bx1, bz1, minPts = 2) {
    let np = 0, off = 0;
    this._qKeep = -1;
    for (let k = 0; k < n; k++) {
      const t = this._cand[k];
      if (!(this.ymin[t] < yhi && this.ymax[t] > ylo) || !this._near(t, bx0, bz0, bx1, bz1)) continue;
      if (off + 16 > this._qb.length) { const g = new Float64Array(this._qb.length * 2); g.set(this._qb); this._qb = g; }
      const pn = this._clip(t, ylo, yhi, this._qb, off);
      if (pn < minPts) continue;
      if (np === this._qo.length) {
        const g = new Int32Array(np * 2); g.set(this._qo); this._qo = g;
        const h = new Uint8Array(np * 2); h.set(this._qn); this._qn = h;
        this._qm = new Uint8Array(np * 2);
        this._qx = new Float64Array(np * 8);
      }
      this._qo[np] = off; this._qn[np] = pn; np++; off += pn * 2;
    }
    return np;
  }

  // the SOLID (px,pz) is inside, among the _bandPolys: the proper polygons
  // that closed-enclose it, grown by every proper polygon touching one
  // already in (a vertex of either within EXIT_EPS of the other, closed) —
  // a triangulated top is one solid, not two triangles. Marks this._qm,
  // returns the member count (0 = not inside a solid).
  _solidOf(np, px, pz) {
    const QB = this._qb, QO = this._qo, QN = this._qn, QM = this._qm, QX = this._qx;
    // each polygon's box grown by BOX_EPS: every closed-containment /
    // EXIT_EPS test below (and _solidExit's) is skipped when the point is
    // outside it — the same answers, fewer _contact calls
    for (let i = 0; i < np; i++) {
      const o = QO[i];
      let x0 = QB[o], z0 = QB[o + 1], x1 = x0, z1 = z0;
      for (let v = 1; v < QN[i]; v++) {
        const x = QB[o + v * 2], z = QB[o + v * 2 + 1];
        if (x < x0) x0 = x; else if (x > x1) x1 = x;
        if (z < z0) z0 = z; else if (z > z1) z1 = z;
      }
      QX[i * 4] = x0 - BOX_EPS; QX[i * 4 + 1] = z0 - BOX_EPS; QX[i * 4 + 2] = x1 + BOX_EPS; QX[i * 4 + 3] = z1 + BOX_EPS;
    }
    let cnt = 0;
    for (let i = 0; i < np; i++) { QM[i] = this._inBox(i, px, pz) && this._encloses(QB, QO[i], QN[i], px, pz) ? 1 : 0; cnt += QM[i]; }
    if (cnt === 0) return 0;
    const touch = (a, b) => {   // a vertex of polygon a in the closed polygon b
      if (QX[a * 4] > QX[b * 4 + 2] || QX[b * 4] > QX[a * 4 + 2] || QX[a * 4 + 1] > QX[b * 4 + 3] || QX[b * 4 + 1] > QX[a * 4 + 3]) return false;
      for (let v = 0; v < QN[a]; v++) {
        if (this._contact(QB, QO[b], QN[b], QB[QO[a] + v * 2], QB[QO[a] + v * 2 + 1], 0, 0).d <= EXIT_EPS) return true;
      }
      return false;
    };
    for (let grew = true; grew;) {
      grew = false;
      for (let i = 0; i < np; i++) {
        if (QM[i] || QN[i] < 3 || !this._encloses(QB, QO[i], QN[i], QB[QO[i]], QB[QO[i] + 1])) continue;
        for (let j = 0; j < np; j++) {
          if (QM[j] !== 1 || !(touch(i, j) || touch(j, i))) continue;
          QM[i] = 1; cnt++; grew = true; break;
        }
      }
    }
    return cnt;
  }

  // is (x,z) in polygon i's box (_solidOf's, grown by BOX_EPS)?
  _inBox(i, x, z) {
    const QX = this._qx;
    return x >= QX[i * 4] && z >= QX[i * 4 + 1] && x <= QX[i * 4 + 2] && z <= QX[i * 4 + 3];
  }

  // is (cx,cz) on the boundary of a member of the solid (_solidOf's marks)?
  _onSolid(np, cx, cz) {
    for (let i = 0; i < np; i++) {
      if (this._qm[i] && this._inBox(i, cx, cz) && Math.abs(this._contact(this._qb, this._qo[i], this._qn[i], cx, cz, cx, cz).d) < EXIT_EPS) return true;
    }
    return false;
  }

  // depenetrate()'s rule: would the centre chord (x0,z0)->(x1,z1) cross a
  // blocker in the band (ylo, yhi)? crosses()' semantics in an explicit
  // band — touching is not crossing, ending strictly inside a proper
  // polygon is — plus the EXIT: the polygons of the solid the start is
  // inside (_solidOf) are not held against it, and a segment / edge crossed
  // on that solid's boundary is its own face (a thin box's side under its
  // top, the riser under a crown): leaving through it is not a crossing.
  _pushCrosses(x0, z0, x1, z1, ylo, yhi) {
    const bx0 = Math.min(x0, x1), bz0 = Math.min(z0, z1), bx1 = Math.max(x0, x1), bz1 = Math.max(z0, z1);
    // depenetrate()'s polygons for this iteration serve when they cover the
    // chord (a superset: those clear of the chord's box cannot meet it)
    const np = this._qKeep >= 0 && bx0 >= this._qx0 && bz0 >= this._qz0 && bx1 <= this._qx1 && bz1 <= this._qz1
      ? this._qKeep : this._bandPolys(this._gather(bx0, bz0, bx1, bz1), ylo, yhi, bx0, bz0, bx1, bz1);
    const QB = this._qb;
    let ns = -1;   // the start's solid, found at the first candidate crossing (most pushes meet none)
    for (let q = 0; q < np; q++) {
      const o = this._qo[q], pn = this._qn[q];
      const endIn = this._contact(QB, o, pn, x1, z1, x0, z0).d < -1e-6;
      const ec = pn === 2 ? 1 : pn;
      for (let i = 0; i < ec; i++) {
        let hit = endIn;
        if (!hit) {
          const j = i + 1 === pn ? 0 : i + 1;
          const ax = QB[o + i * 2], az = QB[o + i * 2 + 1], bx = QB[o + j * 2], bz = QB[o + j * 2 + 1];
          const o1 = (bx - ax) * (z0 - az) - (bz - az) * (x0 - ax);
          const o2 = (bx - ax) * (z1 - az) - (bz - az) * (x1 - ax);
          const o3 = (x1 - x0) * (az - z0) - (z1 - z0) * (ax - x0);
          const o4 = (x1 - x0) * (bz - z0) - (z1 - z0) * (bx - x0);
          if (!(o1 * o2 < 0 && o3 * o4 < 0)) continue;
          hit = o1 / (o1 - o2);    // the crossing's chord parameter (> 0)
        }
        if (ns < 0) ns = this._solidOf(np, x0, z0);
        if (ns && this._qm[q]) break;                     // the solid being left
        if (endIn) return true;                           // ends inside a blocker
        if (!(ns && this._onSolid(np, x0 + (x1 - x0) * hit, z0 + (z1 - z0) * hit))) return true;
      }
    }
    return false;
  }

  // does the hint (hx,hz) give a side: is it outside every solid in the band?
  _hintFree(hx, hz, ylo, yhi) {
    const np = this._bandPolys(this._gather(hx, hz, hx, hz), ylo, yhi, hx, hz, hx, hz);
    for (let i = 0; i < np; i++) if (this._encloses(this._qb, this._qo[i], this._qn[i], hx, hz)) return false;
    return true;
  }

  // the embedded centre (x,z)'s way out of its solid (_solidOf) -> this._hx
  // { nx, nz: the exit face's outward normal, d: the distance from (x,z) to
  // its line }, false when there is none. A face is an edge of a member not
  // covered by another member (a top quad's diagonal, a strip joint are not
  // faces). Toward the hint (hx,hz): the face the chord (x,z) -> hint leaves
  // the solid through (the first member-edge crossing past which the point
  // is in no member). reach > 0 instead: the nearest face within reach.
  _solidExit(x, z, hx, hz, ylo, yhi, reach = 0) {
    const bx0 = Math.min(x, hx) - reach, bz0 = Math.min(z, hz) - reach, bx1 = Math.max(x, hx) + reach, bz1 = Math.max(z, hz) + reach;
    const np = this._bandPolys(this._gather(bx0, bz0, bx1, bz1), ylo, yhi, bx0, bz0, bx1, bz1);
    if (!this._solidOf(np, x, z)) return false;
    const QB = this._qb, QO = this._qo, QN = this._qn, QM = this._qm;
    if (reach > 0) {
      let best = Infinity;
      for (let q = 0; q < np; q++) {
        if (!QM[q]) continue;
        const o = QO[q], pn = QN[q];
        let area = 0;
        for (let i = 0; i < pn; i++) {
          const j = i + 1 === pn ? 0 : i + 1;
          area += QB[o + i * 2] * QB[o + j * 2 + 1] - QB[o + j * 2] * QB[o + i * 2 + 1];
        }
        for (let i = 0; i < pn; i++) {
          const j = i + 1 === pn ? 0 : i + 1;
          const ax = QB[o + i * 2], az = QB[o + i * 2 + 1], ex = QB[o + j * 2] - ax, ez = QB[o + j * 2 + 1] - az;
          const el = Math.hypot(ex, ez);
          if (!(el > 0)) continue;
          const dl = segDist(x, z, ax, az, ax + ex, az + ez);
          if (!(dl < best)) continue;
          const mx = ax + ex / 2, mz = az + ez / 2;
          let covered = false;   // an internal edge: its midpoint is in another member
          for (let r = 0; r < np && !covered; r++) {
            if (r !== q && QM[r] && this._inBox(r, mx, mz) && this._contact(QB, QO[r], QN[r], mx, mz, mx, mz).d <= EXIT_EPS) covered = true;
          }
          if (covered) continue;
          const sg = area > 0 ? 1 : -1;
          best = dl;
          this._hx.nx = sg * ez / el; this._hx.nz = -sg * ex / el;
          this._hx.d = Math.abs((x - ax) * this._hx.nx + (z - az) * this._hx.nz);
        }
      }
      return best < Infinity;
    }
    const L = Math.hypot(hx - x, hz - z);
    let nc = 0;
    for (let q = 0; q < np; q++) {
      if (!QM[q]) continue;
      const o = QO[q], pn = QN[q];
      for (let i = 0; i < pn; i++) {
        const j = i + 1 === pn ? 0 : i + 1;
        const ax = QB[o + i * 2], az = QB[o + i * 2 + 1], bx = QB[o + j * 2], bz = QB[o + j * 2 + 1];
        const o1 = (bx - ax) * (z - az) - (bz - az) * (x - ax);
        const o2 = (bx - ax) * (hz - az) - (bz - az) * (hx - ax);
        const o3 = (hx - x) * (az - z) - (hz - z) * (ax - x);
        const o4 = (hx - x) * (bz - z) - (hz - z) * (bx - x);
        // the ray may start ON the edge, or pass through its end vertex (a
        // joint of the strip: the ray along the joint meets the face there)
        if (!(o1 * o2 <= 0 && o1 !== o2 && o3 * o4 <= 0)) continue;
        if (nc === this._rs.length) {
          const g = new Float64Array(nc * 2); g.set(this._rs); this._rs = g;
          const gp = new Int32Array(nc * 2); gp.set(this._rp); this._rp = gp;
          const ge = new Int32Array(nc * 2); ge.set(this._re); this._re = ge;
        }
        this._rs[nc] = o1 / (o1 - o2) * L; this._rp[nc] = q; this._re[nc] = i; nc++;
      }
    }
    // nearest first (insertion sort: a handful)
    const RS = this._rs, RP = this._rp, RE = this._re;
    for (let a = 1; a < nc; a++) {
      const s = RS[a], p = RP[a], e = RE[a];
      let b = a - 1;
      while (b >= 0 && RS[b] > s) { RS[b + 1] = RS[b]; RP[b + 1] = RP[b]; RE[b + 1] = RE[b]; b--; }
      RS[b + 1] = s; RP[b + 1] = p; RE[b + 1] = e;
    }
    const ux = (hx - x) / L, uz = (hz - z) / L;
    for (let c = 0; c < nc; c++) {
      const s = RS[c] + 1e-5;
      if (s >= L) break;
      const px = x + ux * s, pz = z + uz * s;
      let inAny = false;
      for (let q = 0; q < np && !inAny; q++) {
        if (QM[q] && this._inBox(q, px, pz) && this._contact(QB, QO[q], QN[q], px, pz, px, pz).d < 0) inAny = true;
      }
      if (inAny) continue;
      const o = QO[RP[c]], pn = QN[RP[c]], i = RE[c], j = i + 1 === pn ? 0 : i + 1;
      const ax = QB[o + i * 2], az = QB[o + i * 2 + 1], ex = QB[o + j * 2] - ax, ez = QB[o + j * 2 + 1] - az;
      const el = Math.hypot(ex, ez);
      let nx = -ez / el, nz = ex / el;
      if (nx * ux + nz * uz < 0) { nx = -nx; nz = -nz; }
      this._hx.nx = nx; this._hx.nz = nz; this._hx.d = Math.abs((x - ax) * nx + (z - az) * nz);
      return true;
    }
    return false;
  }

  // apply the exit _solidExit found (this._hx) unless its chord from the
  // call's entry (x0,z0) crosses a blocker: sets out.x/z/moved/hit -> true
  _exitStep(x0, z0, x, z, room, ylo, yhi, out) {
    const E = this._hx;
    const step = Math.min(E.d + SKIN, room);
    const ex = x + E.nx * step, ez = z + E.nz * step;
    if (this._pushCrosses(x0, z0, ex, ez, ylo, yhi)) return false;
    out.x = ex; out.z = ez;
    out.moved += step;
    if (!out.hit) { out.hit = true; out.nx = E.nx; out.nz = E.nz; }
    return true;
  }

  // first crossing (t in (0,1)) of the segment (x0,y0,z0)->(x1,y1,z1) DOWN
  // through a floor (role RL_FLOOR, dir -1) or UP through a ceiling sheet
  // (RL_CEIL, dir +1); sets this._hy to the plane height at the hit.
  _sweepPlanes(x0, y0, z0, x1, y1, z1, roleBit, dir) {
    const n = this._gather(Math.min(x0, x1), Math.min(z0, z1), Math.max(x0, x1), Math.max(z0, z1));
    const yLo = Math.min(y0, y1), yHi = Math.max(y0, y1);
    const bx0 = Math.min(x0, x1), bz0 = Math.min(z0, z1), bx1 = Math.max(x0, x1), bz1 = Math.max(z0, z1);
    let best = Infinity;
    for (let k = 0; k < n; k++) {
      const t = this._cand[k];
      if (!(this.role[t] & roleBit) || this.ymax[t] < yLo || this.ymin[t] > yHi
        || !this._near(t, bx0, bz0, bx1, bz1)) continue;
      const h0 = this._planeY(t, x0, z0), h1 = this._planeY(t, x1, z1);
      if (h0 !== h0) continue;
      const d0 = (y0 - h0) * -dir, d1 = (y1 - h1) * -dir;   // > 0 = the approach side
      if (!(d0 > ON_EPS && d1 < -1e-4)) continue;
      const u = d0 / (d0 - d1);
      if (u >= best) continue;
      const hy = this._heightAt(t, x0 + (x1 - x0) * u, z0 + (z1 - z0) * u);
      if (hy !== hy) continue;
      best = u; this._hy = hy;
    }
    return best;
  }

  // unbounded plane height of triangle t at (x,z) (NaN when vertical)
  _planeY(t, x, z) {
    const V = this.V, I = this.I;
    const a = I[t * 3] * 3, b = I[t * 3 + 1] * 3, c = I[t * 3 + 2] * 3;
    const e1x = V[b] - V[a], e1y = V[b + 1] - V[a + 1], e1z = V[b + 2] - V[a + 2];
    const e2x = V[c] - V[a], e2y = V[c + 1] - V[a + 1], e2z = V[c + 2] - V[a + 2];
    const nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
    if (Math.abs(ny) < 1e-12) return NaN;
    return V[a + 1] - (nx * (x - V[a]) + nz * (z - V[a + 2])) / ny;
  }

  // ---------------------------------------------------------- the frame
  // Move a body of head height H from S=(x0,y0,z0) to E=(x1,y1,z1) (E is the
  // caller's integrated position, already ground-clamped at E). groundFn
  // (x,y,z) -> the caller's full ground there (terrain/spans/structures) or
  // null; used only by the never-crush rule. budget caps the static push.
  //   1. floor/head sweeps: a feet segment that crosses DOWN through a floor,
  //      or a head segment UP through a sheet, stops at the crossing (a jet
  //      diving diagonally onto a deck lands on it)
  //   2. head clamp at the end column; a slot lower than the body is a WALL:
  //      retreat to S (never crush the feet through the ground)
  //   3. lateral slide in substeps of <= SUBSTEP_DY vertical travel, each in
  //      the band at its own height
  //   4. static push-out (spawn, a building push, a crouch standing up)
  //   5. never onto ground over feet + STEP_UP: a push-out (or a slide
  //      deflection) that would is rejected (pushRejected); a push-out never
  //      lowers a short headroom (NO COLLIDER PINCH, below)
  // -> out { x, y, z, hit, nx, nz, land, ceil, blocked, pushRejected }
  move(x0, y0, z0, x1, y1, z1, H, groundFn, budget, out) {
    out = out || {};
    out.hit = false; out.nx = 0; out.nz = 0; out.land = false; out.ceil = false; out.blocked = false;
    out.pushRejected = false;
    out.x = x1; out.y = y1; out.z = z1;
    if (this.nRefs === 0) return out;
    let x = x1, y = y1, z = z1;
    const far = Math.hypot(x1 - x0, z1 - z0) + Math.abs(y1 - y0) > TELEPORT_M;
    if (far) { x0 = x1; y0 = y1; z0 = z1; }
    // 1. vertical sweeps
    if (!far && (x0 !== x1 || y0 !== y1 || z0 !== z1)) {
      const tf = this._sweepPlanes(x0, y0, z0, x1, y1, z1, RL_FLOOR, -1);
      const hyF = this._hy;
      const tc = this._sweepPlanes(x0, y0 + H, z0, x1, y1 + H, z1, RL_CEIL, 1);
      const hyC = this._hy;
      if (tf < Infinity && tf <= tc) {
        x = x0 + (x1 - x0) * tf; z = z0 + (z1 - z0) * tf; y = hyF;
        out.land = true;
      } else if (tc < Infinity) {
        x = x0 + (x1 - x0) * tc; z = z0 + (z1 - z0) * tc; y = hyC - H;
        out.ceil = true;
      }
    }
    // 2. head clamp at the end column (the head sweep above owns crossings;
    // this owns a head that simply rose under a sheet at the end column)
    const c = this.ceilingAt(x, z, y);
    if (c !== null && y + H > c) {
      const g = groundFn ? groundFn(x, y, z) : null;
      if (g === null || g === undefined || c - g >= H + 0.05) {
        y = c - H; out.ceil = true;
      } else {
        out.blocked = true;          // a slot lower than the body is a wall
        x = x0; y = y0; z = z0;
      }
    }
    // 3. lateral, substepped by vertical travel
    const dy = y - y0;
    const N = Math.min(SUBSTEP_MAX, Math.max(1, Math.ceil(Math.abs(dy) / SUBSTEP_DY)));
    let cx = x0, cz = z0;
    const mx = (x - x0) / N, mz = (z - z0) / N;
    for (let k = 1; k <= N; k++) {
      const yk = y0 + dy * (k / N);
      const r = this.slide(cx, cz, mx, mz, yk + STEP_UP + STEP_EPS, yk + H, R_BODY);
      cx = r.x; cz = r.z;
      if (r.hit && !out.hit) { out.hit = true; out.nx = r.nx; out.nz = r.nz; }
    }
    // 4. static push-out at the final height
    const d = this.depenetrate(cx, cz, y + STEP_UP + STEP_EPS, y + H, R_BODY, x0, z0, budget);
    let fx = d.x, fz = d.z;
    // NO COLLIDER PINCH (COL): the push-out never lowers the body's headroom
    // — the point ceiling (ceilingAt) over the ground (groundFn) at the
    // centre — below where the slide left it, where that headroom is short
    // of H + R_BODY (the reach within which a ceiling sheet of up to 45°
    // meets the body's band). A sheet clipped into the head band pushes the
    // body toward the sheet's high side; over ground rising faster than the
    // sheet that is INTO the wedge, and the next frame the re-clamped body
    // meets the sheet again. Recorded case (bakes 3858c98c / 775c54e2,
    // check-traversal 'deck no entry below', path 2075 at (3385, 5408)): a
    // standing jump under a 37.5 % deck whose soffit (3 m under the top)
    // rises slower than the bank under it (46 %): pushed 22 frames running,
    // up to 0.7 m a frame with no input, each lift < STEP_UP (<= 0.32 m),
    // 9.6 m up the bank, the headroom 1.81 -> 1.07 m (the pre-COL collider
    // 8.2 m, to 1.17 m). Refused, the body keeps the slide's column (the
    // walk into less headroom is the player's own, held by 2 and the slide).
    if (groundFn && d.moved > 0) {
      const room = (px, pz) => {
        const c = this.ceilingAt(px, pz, y);
        if (c === null) return Infinity;
        const g = groundFn(px, y, pz);
        return g === null || g === undefined ? Infinity : c - g;
      };
      const r1 = room(fx, fz);
      if (r1 < H + R_BODY && r1 < room(cx, cz)) { out.pushRejected = true; fx = cx; fz = cz; }
    }
    // 5. never lift the body more than a step. The collider's own lateral
    // change (the push-out, a slide deflection) must not set the body on a
    // column whose ground (terrain / spans / structure claim, groundFn) is
    // over feet + STEP_UP: the caller re-clamps to that ground, and the rail
    // window lifted a jumper 2.43 m in one frame. There a sloped girder
    // underside, clipped into the band as a sliver, pushed the body 0.35 m
    // into a bank beside a 6 m skin face. The push is rejected (the slide's
    // column, else the frame's start). The body stays in the sliver and
    // falls clear of it (out.pushRejected: the caller stops the rise).
    if (groundFn && (fx !== x1 || fz !== z1)) {
      const lim = y + STEP_UP + STEP_EPS;
      const over = (px, pz) => { const g = groundFn(px, y, pz); return g !== null && g !== undefined && g > lim; };
      if (over(fx, fz)) {
        out.pushRejected = true;
        if (d.moved > 0 && (cx === x1 && cz === z1 || !over(cx, cz))) { fx = cx; fz = cz; } else { fx = x0; fz = z0; }
      }
    }
    if (d.hit && !out.pushRejected && !out.hit) { out.hit = true; out.nx = d.nx; out.nz = d.nz; }
    out.x = fx; out.y = y; out.z = fz;
    return out;
  }

  // Server check: did the CENTRE path (a point, radius 0) from the last valid
  // position cross a blocker? Conservative on purpose — the band is the
  // crouched body at the HIGHER of the two feet heights, the path is the
  // straight chord, a start already inside a polygon is not held against the
  // report, and touching is not crossing. A legit client slides at R = 0.35
  // off every wall, so its centre chord never meets one.
  crosses(x0, y0, z0, x1, y1, z1, H = SERVER_BAND_H) {
    if (this.nRefs === 0) return false;
    if (Math.hypot(x1 - x0, z1 - z0) > TELEPORT_M) return false;
    const yb = Math.max(y0, y1);
    const ylo = yb + STEP_UP + STEP_EPS, yhi = yb + H;
    const n = this._gather(Math.min(x0, x1), Math.min(z0, z1), Math.max(x0, x1), Math.max(z0, z1));
    const P = this._poly;
    const bx0 = Math.min(x0, x1), bz0 = Math.min(z0, z1), bx1 = Math.max(x0, x1), bz1 = Math.max(z0, z1);
    for (let k = 0; k < n; k++) {
      const t = this._cand[k];
      if (!(this.ymin[t] < yhi && this.ymax[t] > ylo) || !this._near(t, bx0, bz0, bx1, bz1)) continue;
      const pn = this._clip(t, ylo, yhi, P, 0);
      if (pn < 2) continue;
      const c0 = this._contact(P, 0, pn, x0, z0, x0, z0);
      if (c0.d < -1e-6) continue;             // started inside: not held against it
      const c1 = this._contact(P, 0, pn, x1, z1, x0, z0);
      if (c1.d < -1e-6) return true;          // ended inside a proper polygon
      const ne = pn === 2 ? 1 : pn;
      for (let i = 0; i < ne; i++) {
        const j = i + 1 === pn ? 0 : i + 1;
        const ax = P[i * 2], az = P[i * 2 + 1], bx = P[j * 2], bz = P[j * 2 + 1];
        const o1 = (bx - ax) * (z0 - az) - (bz - az) * (x0 - ax);
        const o2 = (bx - ax) * (z1 - az) - (bz - az) * (x1 - ax);
        const o3 = (x1 - x0) * (az - z0) - (z1 - z0) * (ax - x0);
        const o4 = (x1 - x0) * (bz - z0) - (z1 - z0) * (bx - x0);
        if (o1 * o2 < 0 && o3 * o4 < 0) return true;
      }
    }
    return false;
  }

  // Ray (unit direction) vs every collidable triangle, both-sided. Returns
  // the distance to the nearest hit within maxDist, or null (Occlusion's
  // convention). A 2D DDA over the grid, early-out once no later cell can win.
  // skip: role bits whose triangles the ray passes (RL_SHOT_PASS: a bullet)
  raycast(ox, oy, oz, dx, dy, dz, maxDist, skip = 0) {
    if (this.nRefs === 0) return null;
    if (++this._sid === 0x10000) { this.stamp.fill(0); this._sid = 1; }
    const sid = this._sid, st = this.stamp, CELL = this.cell;
    let best = null;
    let cx = Math.floor(ox / CELL), cz = Math.floor(oz / CELL);
    const stepX = dx > 0 ? 1 : -1, stepZ = dz > 0 ? 1 : -1;
    const tdX = dx !== 0 ? Math.abs(CELL / dx) : Infinity;
    const tdZ = dz !== 0 ? Math.abs(CELL / dz) : Infinity;
    let tMaxX = dx !== 0 ? ((cx + (dx > 0 ? 1 : 0)) * CELL - ox) / dx : Infinity;
    let tMaxZ = dz !== 0 ? ((cz + (dz > 0 ? 1 : 0)) * CELL - oz) / dz : Infinity;
    const V = this.V, I = this.I;
    for (;;) {
      const h = this._slot(cx, cz);
      if (h >= 0) {
        for (let k = this.hoff[h], e = k + this.hcnt[h]; k < e; k++) {
          const t = this.cellTris[k];
          if (st[t] === sid) continue;
          st[t] = sid;
          if (this.role[t] & skip) continue;
          const a = I[t * 3] * 3, b = I[t * 3 + 1] * 3, c = I[t * 3 + 2] * 3;
          const e1x = V[b] - V[a], e1y = V[b + 1] - V[a + 1], e1z = V[b + 2] - V[a + 2];
          const e2x = V[c] - V[a], e2y = V[c + 1] - V[a + 1], e2z = V[c + 2] - V[a + 2];
          const px = dy * e2z - dz * e2y, py = dz * e2x - dx * e2z, pz = dx * e2y - dy * e2x;
          const det = e1x * px + e1y * py + e1z * pz;
          if (Math.abs(det) < 1e-12) continue;
          const inv = 1 / det;
          const sx = ox - V[a], sy = oy - V[a + 1], sz = oz - V[a + 2];
          const u = (sx * px + sy * py + sz * pz) * inv;
          if (u < 0 || u > 1) continue;
          const qx = sy * e1z - sz * e1y, qy = sz * e1x - sx * e1z, qz = sx * e1y - sy * e1x;
          const v = (dx * qx + dy * qy + dz * qz) * inv;
          if (v < 0 || u + v > 1) continue;
          const tt = (e2x * qx + e2y * qy + e2z * qz) * inv;
          if (tt < 0 || tt > maxDist) continue;
          if (best === null || tt < best) best = tt;
        }
      }
      const tNext = Math.min(tMaxX, tMaxZ);
      if (tNext > maxDist || (best !== null && best <= tNext)) return best;
      if (tMaxX < tMaxZ) { cx += stepX; tMaxX += tdX; } else { cz += stepZ; tMaxZ += tdZ; }
    }
  }

  // bytes held: the index this module built, and the artifact arrays it reads
  memory() {
    const s = this.str;
    const index = this.ymin.byteLength + this.ymax.byteLength + this.role.byteLength
      + this.stamp.byteLength + this.hkey.byteLength + this.hoff.byteLength
      + this.hcnt.byteLength + this.cellTris.byteLength + this._cand.byteLength
      + this._pb.byteLength + this._po.byteLength + this._pn.byteLength
      + this._kd.byteLength + this._kx.byteLength + this._kz.byteLength
      + this._qb.byteLength + this._qo.byteLength + this._qn.byteLength + this._qm.byteLength + this._qx.byteLength
      + this._rs.byteLength + this._rp.byteLength + this._re.byteLength;
    const artifact = s ? s.bytes + s.nStruct * 49 : 0;   // file buffer (views) + per-structure arrays
    return { index, artifact, total: index + artifact, cells: this.nCells, refs: this.nRefs,
      tris: this.nTris, collidable: this.nColl, max_cell: this.maxCell };
  }
}

module.exports = {
  StructCollider, R_BODY, STEP_UP, STEP_EPS, SLOPE_WALK_DEG, SLOPE_STAIR_DEG, DEFAULT_CELL,
  SERVER_BAND_H, RL_SHOT_PASS,
};
